import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { WorkspaceRoleSlugSchema } from '../identity/members.schemas';

/**
 * Bus events emitted by the workspace-tier member command handlers.
 *
 * `memberInvited` carries email + name on the payload so a future
 * communications subscriber can dispatch the invite email without
 * re-querying the identity database — same pattern admin uses for
 * `admin.adminAdded`. The other payloads carry ids only, per ADR 0015 §PII.
 */

export const MemberInvitedPayloadSchema = z.object({
  memberId: z.uuid(),
  workspaceId: z.uuid(),
  email: z.email(),
  firstName: z.string(),
  lastName: z.string(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type MemberInvitedPayload = z.infer<typeof MemberInvitedPayloadSchema>;

export const MemberUpdatedPayloadSchema = z.object({
  memberId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type MemberUpdatedPayload = z.infer<typeof MemberUpdatedPayloadSchema>;

/**
 * Emitted whenever a member's effective authority or Standort visibility changes
 * — workspace role, organisation roles, or affiliation. Distinct from
 * `memberUpdated` (profile data) so consumers that care only about access do not
 * react to a renamed member. Ids only, per ADR 0015 §PII.
 */
export const MemberAccessChangedPayloadSchema = z.object({
  memberId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
  workspaceRoles: z
    .object({
      before: z.array(WorkspaceRoleSlugSchema),
      after: z.array(WorkspaceRoleSlugSchema),
    })
    .optional(),
});

export type MemberAccessChangedPayload = z.infer<typeof MemberAccessChangedPayloadSchema>;

export const MemberDeactivatedPayloadSchema = z.object({
  memberId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
  workspaceRolesBefore: z.array(WorkspaceRoleSlugSchema).optional(),
});

export type MemberDeactivatedPayload = z.infer<typeof MemberDeactivatedPayloadSchema>;

export const MemberReactivatedPayloadSchema = z.object({
  memberId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type MemberReactivatedPayload = z.infer<typeof MemberReactivatedPayloadSchema>;
